/**
 * Size Guide drawer — right-side slide-in on the product page.
 *
 * The table is rendered server-side in centimetres (see snippets/size-guide-drawer.liquid).
 * Inches are derived here from the `data-cm` attribute on each cell, so the CM/IN
 * toggle can never disagree with the stored data and only one unit is ever entered.
 */
class SizeGuideDrawer extends HTMLElement {
  constructor() {
    super();

    this.inner = this.querySelector(".size-guide-drawer__inner");

    this.addEventListener("keyup", (event) => {
      if (event.code === "Escape") this.close();
    });

    // Close on any click that lands outside the panel. Checking against the
    // panel (rather than listening on the overlay) keeps this working no matter
    // how the overlay itself is painted.
    this.addEventListener("click", (event) => {
      if (this.inner && !this.inner.contains(event.target)) this.close();
    });

    this.querySelector("[data-size-guide-close]")?.addEventListener(
      "click",
      this.close.bind(this)
    );

    this.querySelectorAll("[data-unit]").forEach((button) => {
      button.addEventListener("click", () => this.setUnit(button.dataset.unit));
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.activeElement = triggeredBy;

    // Matches the cart drawer: a tick's delay so the transition actually runs.
    setTimeout(() => {
      this.classList.add("active");
    });

    this.addEventListener(
      "transitionend",
      () => {
        if (typeof trapFocus === "function") {
          trapFocus(this.inner, this.querySelector("[data-size-guide-close]"));
        } else {
          this.inner?.focus();
        }
      },
      { once: true }
    );
  }

  close() {
    this.classList.remove("active");

    if (typeof removeTrapFocus === "function") {
      removeTrapFocus(this.activeElement);
    } else {
      this.activeElement?.focus();
    }
  }

  setUnit(unit) {
    const toInches = unit === "in";

    this.querySelectorAll("[data-unit]").forEach((button) => {
      button.classList.toggle("is-active", button.dataset.unit === unit);
    });

    this.querySelectorAll("[data-cm]").forEach((cell) => {
      const cm = parseFloat(cell.dataset.cm);
      if (Number.isNaN(cm)) return;
      cell.textContent = toInches ? `${(cm / 2.54).toFixed(2)}"` : String(cm);
    });
  }
}

customElements.define("size-guide-drawer", SizeGuideDrawer);

// Any [data-size-guide-open] trigger opens the drawer. Delegated so triggers
// that get re-rendered by a variant change keep working.
document.addEventListener("click", (event) => {
  const trigger = event.target.closest("[data-size-guide-open]");
  if (!trigger) return;

  event.preventDefault();
  document.querySelector("size-guide-drawer")?.open(trigger);
});
